// App-Icon-Knopf (Handoff Tickets): Icon-Kachel mit Verlauf wie ein iOS-App-Icon + kleines Label darunter
// (Werkzeugleiste) bzw. größere Kachel für Aktionskacheln (Nachtragen, Nachweis, Aktenschrank).
import type { CSSProperties, ReactNode } from 'react';
import type { Icon } from '@phosphor-icons/react';

/** Verläufe der App-Icons (Prototyp: iOS-Systemfarben). */
export const APP_VERLAUF = {
  orange: { verlauf: 'linear-gradient(160deg,#ffb340,#ff9500)', ink: '#fff' },
  blau: { verlauf: 'linear-gradient(160deg,#5ac8fa,#0a84ff)', ink: '#fff' },
  gold: { verlauf: 'linear-gradient(160deg,#f1dcb4,#d4ad73 55%,#9c7440)', ink: '#231806' },
  gruen: { verlauf: 'linear-gradient(160deg,#4cd964,#28a745)', ink: '#fff' },
  grau: { verlauf: 'linear-gradient(160deg,#a1a1a6,#6e6e73)', ink: '#fff' },
  rot: { verlauf: 'linear-gradient(160deg,#ff6961,#ff3b30)', ink: '#fff' },
  lila: { verlauf: 'linear-gradient(160deg,#c49bff,#8a63d2)', ink: '#fff' },
} as const;
export type AppFarbe = keyof typeof APP_VERLAUF;

/** Nur die Icon-Kachel (24 / 28 / 30 px). */
export function AppIcon({ icon: I, farbe, groesse = 24 }: { icon: Icon; farbe: AppFarbe; groesse?: 24 | 28 | 30 }) {
  const v = APP_VERLAUF[farbe];
  return (
    <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{
      width: groesse, height: groesse, borderRadius: groesse === 24 ? 7 : groesse === 28 ? 8 : 9,
      background: v.verlauf, color: v.ink,
      boxShadow: 'inset 0 0.5px 0 rgba(255,255,255,0.45), 0 3px 8px -3px rgba(0,0,0,0.35)',
    }}>
      <I size={groesse === 24 ? 16 : 18} weight="fill" />
    </span>
  );
}

export interface AppIconKnopfProps {
  icon: Icon;
  farbe: AppFarbe;
  label: string;
  onClick: () => void;
  tip?: string;
  /** nur das Icon (schmale Breiten) – Label bleibt für Screenreader */
  nurIcon?: boolean;
  gesperrt?: boolean;
}

/** Werkzeugleisten-Knopf: Icon über Label, Hover --hover, Drücken scale .94. */
export function AppIconKnopf({ icon, farbe, label, onClick, tip, nurIcon = false, gesperrt = false }: AppIconKnopfProps) {
  return (
    <button type="button" onClick={onClick} disabled={gesperrt} aria-label={label} data-tip={tip}
      className="immo-app-knopf inline-flex flex-col items-center justify-center"
      style={{ minWidth: nurIcon ? 40 : 50, height: nurIcon ? 40 : 46, gap: 4, padding: '0 6px', borderRadius: 14, border: 'none',
        background: 'transparent', color: 'var(--ink2)', fontFamily: 'inherit', cursor: gesperrt ? 'not-allowed' : 'pointer', opacity: gesperrt ? 0.45 : 1 }}>
      <AppIcon icon={icon} farbe={farbe} />
      {!nurIcon && <span aria-hidden style={{ fontSize: 10, fontWeight: 700, lineHeight: 1 }}>{label}</span>}
    </button>
  );
}

/** Behälter der Werkzeugleiste (--fill2, r22). */
export function AppIconLeiste({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div role="toolbar" className="inline-flex items-center" style={{ padding: 4, gap: 2, borderRadius: 22, background: 'var(--fill2)', ...style }}>{children}</div>;
}

/** Größere Aktionskachel (Icon 30 + Titel + Unterzeile), z. B. „Nachtragen – Zeit ohne Timer“. */
export function AppKachel({ icon, farbe, titel, unter, onClick, tip, gesperrt = false }: {
  icon: Icon; farbe: AppFarbe; titel: string; unter?: string; onClick: () => void; tip?: string; gesperrt?: boolean;
}) {
  return (
    <button type="button" onClick={onClick} disabled={gesperrt} data-tip={tip}
      className="immo-kachel-knopf flex min-w-0 items-center text-left"
      style={{ gap: 10, padding: '10px 12px', borderRadius: 16, border: 'none', background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)',
        color: 'var(--ink)', fontFamily: 'inherit', cursor: gesperrt ? 'not-allowed' : 'pointer', opacity: gesperrt ? 0.5 : 1 }}>
      <AppIcon icon={icon} farbe={farbe} groesse={30} />
      <span className="flex min-w-0 flex-col">
        <span className="truncate" style={{ fontSize: 13.5, fontWeight: 800 }}>{titel}</span>
        {unter && <span className="truncate" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)' }}>{unter}</span>}
      </span>
    </button>
  );
}
